import React, { useEffect, useState } from "react"
import cx from "classnames"
import { toast } from "react-toastify"
import format from "date-fns/format"
import { toZonedTime } from "date-fns-tz"
import { PiThumbsUpDuotone, PiHandPalmDuotone, PiArrowCounterClockwiseBold } from "react-icons/pi"

import Panel from "../Panel"
import Button from "../Button"
import apiSignOff from "../../services/signOff/signOff.api"
import { getObjectId } from "../../utils/utils"
import { addParam } from "../History"
import { SIGN_OFF_STATUS, SIGN_OFF_STATUS_LABEL, SIGN_OFF_TYPE } from "../../utils/signOffs"
import { SignOffStatus } from "./SignOffs"
import "./SignOffs.scss"

const when = (date) => (date ? format(toZonedTime(new Date(date)), "eee. MMM d, yyyy h:mma") : "")

/**
 * One sign-off on its own: what is being signed off (a snapshot the server
 * reads for whoever may see it, so the approver needs no place on the
 * project), who asked, what happened so far, and the buttons this person
 * has. Opened from a notification, an email (?sign-off=<id>) or the inbox.
 *
 * @param orgId {String}
 * @param signOffId {String}
 * @param [onChanged] {function(Object)} after every write
 */
const SignOffView = ({ app, orgId, signOffId, open, onClose, onChanged }) => {
    const [signOff, setSignOff] = useState(null)
    const [error, setError] = useState(null)
    const [busy, setBusy] = useState(false)
    const [note, setNote] = useState("")
    const [rejecting, setRejecting] = useState(false)
    const id = getObjectId(orgId)

    useEffect(() => {
        let cancelled = false

        setSignOff(null)
        setError(null)
        setRejecting(false)
        setNote("")

        if (!id || !signOffId) return undefined

        apiSignOff
            .get(id, signOffId)
            .then((res) => {
                if (!cancelled) setSignOff(res)
            })
            .catch((err) => {
                if (!cancelled) setError(err?.message || "This sign-off could not be opened.")
            })

        return () => {
            cancelled = true
        }
    }, [id, signOffId])

    const run = async (fn) => {
        if (busy) return

        setBusy(true)

        try {
            const res = await fn()

            if (res) {
                // The read endpoint carries the subject and the abilities; the
                // writes carry the sign-off only, so reread for both.
                const fresh = await apiSignOff.get(id, signOffId).catch(() => ({ ...signOff, ...res }))

                setSignOff(fresh)
                onChanged?.(fresh)
            }
        } catch (err) {
            toast.error(err?.message || "Sorry, that didn't work. Please try again.")
        } finally {
            setBusy(false)
            setRejecting(false)
        }
    }

    // The app's navigator sits above panels; hide it while this one is up
    useEffect(() => {
        if (!open || !app?.hideKnobs) return undefined

        // Opened over the inbox, which hid it already: closing must not bring it back
        const wasHidden = app.state.hideDamKnob

        app.hideKnobs()

        return () => {
            if (!wasHidden) app.showKnobs()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const can = signOff?.can || {}
    const subject = signOff?.subject
    const isTask = signOff?.type === SIGN_OFF_TYPE.ACTION_ITEM && !!signOff.refId
    const isMission = signOff?.type === SIGN_OFF_TYPE.MISSION
    const missionId = getObjectId(isMission ? signOff?.refId : subject?.mission?._id || subject?.mission)
    const missionTitle = isMission ? subject?.title : subject?.mission?.title

    // The task (?action-item) or the mission (?mission) straight from here: no org screen needed
    const openParam = (param, value) => {
        onClose()
        addParam(param, getObjectId(value))
    }

    return (
        // Above the app navigator (998) and the inbox card it opens from (9999)
        <Panel
            open={open}
            onClose={onClose}
            maxWidth={720}
            zIndex={10000}
            className="x-sign-off-view-panel"
            portal
        >
            <div className="x-sign-off-view">
                {error && <p>{error}</p>}

                {!signOff && !error && <p>Loading...</p>}

                {signOff && (
                    <>
                        <div className="x-sign-off-view-head">
                            <h2>{signOff.description || "Sign-off"}</h2>
                            <SignOffStatus signOff={signOff} />
                        </div>
                        <p>
                            Requested by <strong>{signOff.requestedBy?.name || "somebody"}</strong>
                            {signOff.createdAt && <> on {when(signOff.createdAt)}</>}
                            {signOff.approver?.name && (
                                <>
                                    {" "}
                                    · Approver <strong>{signOff.approver.name}</strong>
                                </>
                            )}
                            {!signOff.approver?.ref && <> · No approver yet</>}
                        </p>

                        {signOff.note && (
                            <div className="x-sign-off-note">
                                <strong>{signOff.status === SIGN_OFF_STATUS.REJECTED ? "Rejected: " : "Note: "}</strong>
                                {signOff.note}
                            </div>
                        )}

                        {isTask && (
                            <p>
                                Task:{" "}
                                <span className="x-link" onClick={() => openParam("action-item", signOff.refId)}>
                                    {subject?.title || signOff.title || "Open the task"}
                                </span>
                            </p>
                        )}
                        {missionId && (
                            <p>
                                Mission:{" "}
                                <span className="x-link" onClick={() => openParam("mission", missionId)}>
                                    {missionTitle || "Open the mission"}
                                </span>
                            </p>
                        )}

                        {!subject && <p>The thing this was about is no longer available.</p>}

                        {(can.decide || (can.withdraw && signOff.status === SIGN_OFF_STATUS.APPROVED)) && (
                            <div className="x-sign-off-decide">
                                {can.decide && !rejecting && (
                                    <>
                                        <p>
                                            {signOff.status === SIGN_OFF_STATUS.READY
                                                ? "This is ready for your sign-off."
                                                : "This is waiting on you."}
                                        </p>
                                        <textarea
                                            placeholder="A note with your decision (optional for an approval)"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                    </>
                                )}

                                {can.decide && rejecting && (
                                    <>
                                        <p>Say why it is rejected and what has to change.</p>
                                        <textarea
                                            autoFocus
                                            placeholder="Why is it rejected?"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                    </>
                                )}

                                <div className="x-sign-off-decide-buttons">
                                    {can.decide && !rejecting && (
                                        <>
                                            <Button
                                                icon={true}
                                                className={cx({ disabled: busy })}
                                                onClick={(e) =>
                                                    run(() =>
                                                        apiSignOff.decide(id, signOff._id, {
                                                            decision: "approved",
                                                            note: note.trim() || undefined,
                                                        }),
                                                    ).then(() =>
                                                        app?.doneBurst
                                                            ?.tune({ x: e.pageX, y: e.pageY })
                                                            .generate()
                                                            .replay(),
                                                    )
                                                }
                                            >
                                                <PiThumbsUpDuotone /> Approve
                                            </Button>
                                            <Button
                                                icon={true}
                                                className={cx("transparent", { disabled: busy })}
                                                onClick={() => setRejecting(true)}
                                            >
                                                <PiHandPalmDuotone /> Reject
                                            </Button>
                                        </>
                                    )}

                                    {can.decide && rejecting && (
                                        <>
                                            <Button
                                                className={cx("red", { disabled: busy || !note.trim() })}
                                                onClick={() =>
                                                    run(() =>
                                                        apiSignOff.decide(id, signOff._id, {
                                                            decision: "rejected",
                                                            note: note.trim(),
                                                        }),
                                                    )
                                                }
                                            >
                                                Reject
                                            </Button>
                                            <Button
                                                className={cx("transparent", { disabled: busy })}
                                                onClick={() => setRejecting(false)}
                                            >
                                                Never mind
                                            </Button>
                                        </>
                                    )}

                                    {can.withdraw && signOff.status === SIGN_OFF_STATUS.APPROVED && (
                                        <Button
                                            icon={true}
                                            className={cx("transparent", { disabled: busy })}
                                            onClick={() =>
                                                app.confirm({
                                                    comp: (
                                                        <p>
                                                            Take your approval back? The request goes back to ready for
                                                            approval.
                                                        </p>
                                                    ),
                                                    onYes: () => run(() => apiSignOff.withdraw(id, signOff._id)),
                                                })
                                            }
                                        >
                                            <PiArrowCounterClockwiseBold /> Withdraw approval
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {signOff.history?.length > 0 && (
                            <div className="x-sign-off-history">
                                <h3>What happened</h3>
                                <ul>
                                    {signOff.history
                                        .slice()
                                        .reverse()
                                        .map((entry, i) => (
                                            <li key={i} className="dna-smaller-text">
                                                <span className="x-sign-off-history-when dna-light-text">
                                                    {when(entry.at)}
                                                </span>
                                                <span>
                                                    <strong>
                                                        {SIGN_OFF_STATUS_LABEL[entry.status] || entry.status}
                                                    </strong>
                                                    {entry.by?.name ? ` by ${entry.by.name}` : ""}
                                                    {entry.note ? ` — ${entry.note}` : ""}
                                                </span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Panel>
    )
}

export default SignOffView
